// Cross-cutting UXCam instrumentation: every toast the user sees, every failed
// API call, and every uncaught JS error becomes a session event, so "did they
// hit an issue?" and "did they hit an error?" are answerable without touching
// each of the ~40 individual toast call sites.
import { trackUxcamEvent, trackUxcamError, setUxcamProperties } from "./uxcam";

// Toast variants that mean "the user was blocked or something went wrong".
const ISSUE_VARIANTS = ["danger", "warning"];

let issueCount = 0;
let errorCount = 0;

export function installUxcamHooks(Vue, axios) {
  installToastTracking(Vue);
  installVueErrorTracking(Vue);
  installWindowErrorTracking();
  installApiErrorTracking(axios);
}

//
// BootstrapVue creates one `_bv__toast` per component in `beforeCreate` and
// `$bvToast` is a getter that returns it, so a `created` hook can wrap the
// instance method and the wrapper survives for that component's lifetime.
//
function installToastTracking(Vue) {
  Vue.mixin({
    created() {
      let bvToast;
      try {
        bvToast = this.$bvToast;
      } catch (e) {
        return;
      }
      if (!bvToast || typeof bvToast.toast !== "function") return;
      if (bvToast.__uxcamWrapped) return;

      const original = bvToast.toast.bind(bvToast);
      bvToast.toast = (message, options = {}) => {
        recordToast(message, options);
        return original(message, options);
      };
      bvToast.__uxcamWrapped = true;
    },
  });
}

function recordToast(message, options) {
  const variant = (options && options.variant) || "default";
  const title = typeof options?.title === "string" ? options.title : null;
  const body = typeof message === "string" ? message : null;

  if (ISSUE_VARIANTS.indexOf(variant) !== -1) {
    issueCount += 1;
    trackUxcamEvent("user_issue", { variant, title, message: body });
    setUxcamProperties({ issue_count: issueCount, encountered_issue: true });
    return;
  }

  trackUxcamEvent("app_notice", { variant, title, message: body });
}

function installVueErrorTracking(Vue) {
  const previous = Vue.config.errorHandler;

  Vue.config.errorHandler = (err, vm, info) => {
    countError();
    trackUxcamError("vue", err, {
      component: vm?.$options?.name || vm?.$options?._componentTag || null,
      lifecycle: info,
    });

    if (typeof previous === "function") {
      previous(err, vm, info);
    } else {
      // Vue swallows the error once a handler is installed; keep the console
      // trace the app had before instrumentation.
      console.error(err);
    }
  };
}

function installWindowErrorTracking() {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    if (isRecorderNoise(event.filename, event.error)) return;
    countError();
    trackUxcamError("window", event.error || event.message, {
      file: event.filename,
      line: event.lineno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isRecorderNoise(null, event.reason)) return;
    countError();
    trackUxcamError("unhandled_rejection", event.reason);
  });
}

// The recorder throws from inside its own bundle - an unguarded
// `CSSStyleSheet.cssRules` read on a cross-origin sheet rejects on nearly every
// page load. Counting those would set `encountered_error` on every session and
// destroy the signal the property exists to carry, so drop anything whose
// origin is the SDK rather than the app.
function isUxcamHost(hostname) {
  if (typeof hostname !== "string") return false;
  const host = hostname.toLowerCase();
  return host === "uxcam.com" || host.endsWith(".uxcam.com");
}

function isUxcamUrl(value) {
  if (typeof value !== "string" || !value) return false;
  try {
    const { hostname } = new URL(value);
    return isUxcamHost(hostname);
  } catch (e) {
    return false;
  }
}

function isRecorderNoise(file, error) {
  if (isUxcamUrl(file)) return true;

  const stack = error && typeof error.stack === "string" ? error.stack : "";
  const urls = stack.match(/https?:\/\/[^\s)]+/gi) || [];
  return urls.some((url) => isUxcamUrl(url));
}

function installApiErrorTracking(axios) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      countError();
      trackUxcamError("api", error, {
        method: error?.config?.method?.toUpperCase(),
        // `url` is the API path (may carry an sfid), never the request body.
        url: error?.config?.url,
        status: error?.response?.status ?? null,
      });
      return Promise.reject(error);
    }
  );
}

function countError() {
  errorCount += 1;
  setUxcamProperties({ error_count: errorCount, encountered_error: true });
}
